import { Fragment, useCallback, useEffect, useMemo, useRef } from 'react';
import { __OPERATION__BATCH__ } from 'shell/constants/environment';
import Table from 'shell/components/shared/table';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { generatePath, useNavigate } from 'react-router-dom';
import { HttpResponseList } from 'shell/models/http-response';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { initialValuesProcessingJob } from '..';
import { useFormik } from 'formik';
import { FiltersProcessingJob, ProcessingJobClient } from './types';
import { transformDataset } from './utils';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { CommonProcessingJobCrumbs } from '../constants';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import { authsObj, headCells, validationSchemaBatchFilter } from './constants';
import FilterProcessingJob from 'system/components/processing-job/list';
import { Typography } from '@mui/material';
import { noop } from 'shell/utils/noop';
import { ProcessingJob } from 'shell/models/job-execution';
import { DateTime } from 'luxon';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';

const ProcessingJobList = (): React.ReactElement => {
  const totalElementsRef = useRef<number>(0);

  const [processingJobs, getProcessingJobs] = useHttpFetch<
    HttpResponseList<ProcessingJob>,
    Array<ProcessingJobClient>
  >(`${__OPERATION__BATCH__}/executions`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
      data: [],
    },
    transformer: (response) => {
      totalElementsRef.current = response.data.payload?.pageInfo?.totalCount || 0;

      return response.data?.payload?.list.map((data: ProcessingJob) => {
        const itemResult: ProcessingJobClient = {
          hidden: {
            edit: true,
            delete: true,
          },
          id: data?.id,
          //todo
          batchName: data?.jobName ? data.jobName : '',
          completedAt: data?.completedAt
            ? DateTime.fromISO(data?.completedAt).toFormat('dd/MM/yyyy HH:mm:ss')
            : '',
          startedAt: data?.startedAt
            ? DateTime.fromISO(data?.startedAt).toFormat('dd/MM/yyyy HH:mm:ss')
            : '',
          state: data?.status ? data.status : '',
          numError: data?.outputParameters?.ERRORS,
          numInput: data?.outputParameters?.INPUTS,
          numOutput: data?.outputParameters?.OUTPUTS,
          numWarning: data?.outputParameters?.WARNINGS,
          sent: data?.startedAt
            ? DateTime.fromISO(data?.startedAt).toFormat('dd/MM/yyyy HH:mm:ss')
            : '',
        };
        return itemResult;
      });
    },
  });

  const filterTable = useFilterTable<FiltersProcessingJob>();

  const onSubmit = useCallback(
    (formValues: FiltersProcessingJob) => {
      const queryString = transformDataset({ ...formValues, page: 0 });
      filterProcessingJobForm.setFieldValue('page', 0);
      getProcessingJobs({
        params: {},
        paramsSerializer: () => queryString,
      });
    },
    [getProcessingJobs]
  );

  const filterProcessingJobForm = useFormik<FiltersProcessingJob>({
    initialValues: initialValuesProcessingJob,
    validationSchema: validationSchemaBatchFilter,
    onSubmit,
  });

  useEffect(() => {
    filterProcessingJobForm.setValues(filterTable.values || initialValuesProcessingJob);
    const queryString = transformDataset(filterTable.values || initialValuesProcessingJob);
    getProcessingJobs({
      params: {},
      paramsSerializer: () => queryString,
    });
  }, []);

  useEffect(() => {
    return () => filterTable.setValues(filterProcessingJobForm.values);
  }, [filterProcessingJobForm.values]);

  const count: number = useMemo(() => {
    return totalElementsRef.current || 0;
  }, [processingJobs.data]);

  const navigate = useNavigate();

  const auths = useActionAuthorization(authsObj);

  const onVisibilityPress = useCallback(
    (cell): Promise<ProcessingJobClient> => {
      return new Promise((resolve) => {
        navigate(
          generatePath('/sistema/elaborazione-job/dettaglio/:id', { id: cell.id.toString() })
        );
        resolve(cell);
      });
    },
    [navigate]
  );

  const handleChangePage = useCallback(
    (newPage) => {
      const newDataset = { ...filterProcessingJobForm.values, page: newPage || 0 };
      const queryString = transformDataset(newDataset);
      (
        getProcessingJobs({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<HttpResponseList<ProcessingJob>, Array<ProcessingJobClient>>
      ).then(() => {
        filterProcessingJobForm.setFieldValue('page', newPage);
      });
    },
    [getProcessingJobs, filterProcessingJobForm.setFieldValue, filterProcessingJobForm.values]
  );

  const handleChangeRowsPerPage = useCallback(
    (rowsPerPage) => {
      const newDataset = {
        ...filterProcessingJobForm.values,
        rowsPerPage: rowsPerPage || 0,
        page: 0,
      };
      const queryString = transformDataset(newDataset);
      (
        getProcessingJobs({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<HttpResponseList<ProcessingJob>, Array<ProcessingJobClient>>
      ).then(() => {
        filterProcessingJobForm.setValues(newDataset);
      });
    },
    [getProcessingJobs, filterProcessingJobForm.setFieldValue, filterProcessingJobForm.values]
  );

  const onSort = useCallback(
    (orders) => {
      const newDataset = { ...filterProcessingJobForm.values, orders };
      const queryString = transformDataset(newDataset);
      (
        getProcessingJobs({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<HttpResponseList<ProcessingJob>, Array<ProcessingJobClient>>
      ).then(() => {
        filterProcessingJobForm.setFieldValue('orders', orders);
      });
    },
    [getProcessingJobs, filterProcessingJobForm.setFieldValue, filterProcessingJobForm.values]
  );

  return (
    <Fragment>
      <Breadcrumbs items={CommonProcessingJobCrumbs} />
      <Typography
        color="secondary.main"
        style={{
          fontSize: '26px',
        }}
      >
        Elaborazioni Job
      </Typography>
      <div className="pmi-my-3">
        <FilterProcessingJob filterBatchForm={filterProcessingJobForm} />
      </div>
      {processingJobs.data ? (
        (processingJobs.data as Array<ProcessingJobClient>)?.length > 0 ? (
          <Table
            onSort={onSort}
            head={headCells}
            orders={filterProcessingJobForm.values.orders}
            activeActions={auths}
            page={filterProcessingJobForm.values.page}
            rowsPerPage={filterProcessingJobForm.values.rowsPerPage}
            body={(processingJobs.data as Array<ProcessingJobClient>) || []}
            onVisibilityPress={onVisibilityPress}
            onEditPress={noop}
            onTrashPress={noop}
            isServer
            isNotLayoutFixed
            count={count || 0}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ariaLabel="Lista eleborazione Job"
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
    </Fragment>
  );
};

ProcessingJobList.displayName = 'ProcessingJob List';

export default ProcessingJobList;
